/**
 * Admin-only: who can sign in to Northstar, and at what level.
 *
 * Access is an Entra app role assignment on the Northstar service principal
 * (docs/MCP.md, step 6). This panel lists those assignments and lets an admin
 * add a person at either tier or revoke one, through the backend's Graph
 * client. Entra stays the source of truth: a change lands with the person's
 * next token, so someone already signed in keeps their old access for up to an
 * hour, and the panel says so.
 */
import { useEffect, useState } from "react";
import { ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccess, useAccessPeople, useGrantAccess, useRevokeAccess } from "@/lib/useAdmin";
import type { AccessAssignment, AccessPerson, AccessRole } from "@/lib/types";
import { cn } from "@/lib/utils";

const TIERS: { role: AccessRole; label: string }[] = [
  { role: "launcher", label: "Project Launcher only" },
  { role: "member", label: "Full access" },
  { role: "agent", label: "AI agent" },
];

/** Case-insensitive, like the backend's own-row check: Entra preserves whatever
 *  casing an id was issued with, and a mismatch here would offer a Revoke the
 *  API then refuses. */
function isSelf(a: AccessAssignment, selfOid?: string): boolean {
  return Boolean(selfOid) && a.principal_id.toLowerCase() === selfOid!.toLowerCase();
}

function principalLabel(a: AccessAssignment): string {
  return a.upn ?? a.display_name ?? a.principal_id;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

/** One assignment: who, which tier, since when, and a two-step revoke. */
function AssignmentRow({ assignment, isSelf }: { assignment: AccessAssignment; isSelf: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const revoke = useRevokeAccess();
  const group = assignment.principal_type === "Group";

  return (
    <div className="flex flex-col gap-1 border-b border-bw-blue-muted/30 py-2 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="flex items-center gap-2 truncate font-sans text-sm font-semibold text-bw-navy">
            {group && <Users className="h-4 w-4 shrink-0 text-bw-blue" aria-label="Group" />}
            {assignment.display_name ?? principalLabel(assignment)}
            {isSelf && (
              <span className="rounded-pill bg-bw-blue/10 px-2 py-0.5 text-[10px] font-normal">you</span>
            )}
          </span>
          <span className="font-sans text-xs text-bw-ink-muted">
            {assignment.upn ?? (group ? "Every direct member of this group" : assignment.principal_id)}
            {assignment.created_at && <> · since {formatWhen(assignment.created_at)}</>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-pill px-2 py-0.5 font-sans text-[10px] font-semibold",
              assignment.role === "launcher" ? "bg-bw-blue-muted/20 text-bw-navy" : "bg-bw-navy text-white",
            )}
          >
            {assignment.role_label}
          </span>
          {isSelf ? null : confirming ? (
            <>
              <Button
                variant="destructive"
                size="sm"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(assignment.id, { onSettled: () => setConfirming(false) })}
              >
                {revoke.isPending ? "Revoking…" : "Confirm revoke"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
              <Trash2 className="h-4 w-4" aria-hidden />
              Revoke
            </Button>
          )}
        </div>
      </div>
      {confirming && (
        <p className="font-sans text-xs text-bw-ink-muted">
          {group
            ? "Removes access for everyone in this group at once. "
            : `${principalLabel(assignment)} will no longer be able to sign in at this level. `}
          Takes effect at their next sign-in.
        </p>
      )}
      {revoke.isError && (
        <span className="font-sans text-xs text-bw-danger">
          {typeof (revoke.error.body as { detail?: unknown })?.detail === "string"
            ? String((revoke.error.body as { detail: string }).detail)
            : "Couldn't revoke that. Try again."}
        </span>
      )}
    </div>
  );
}

/** Search the tenant, then grant at either tier. */
function AddPerson() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [picked, setPicked] = useState<AccessPerson | null>(null);
  const grant = useGrantAccess();

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query), 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  const people = useAccessPeople(picked ? "" : debounced);

  return (
    <div className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3">
      <label className="font-sans text-xs font-semibold uppercase tracking-wide text-bw-ink-muted" htmlFor="access-q">
        Add someone
      </label>
      <input
        id="access-q"
        className="rounded-pill border border-bw-blue-muted/50 px-3 py-1.5 font-sans text-sm"
        placeholder="Name or email in the BluWave tenant"
        value={picked ? (picked.upn ?? picked.display_name ?? "") : query}
        onChange={(event) => {
          setPicked(null);
          setQuery(event.target.value);
        }}
        autoComplete="off"
      />
      {!picked && people.isLoading && <span className="font-sans text-xs text-bw-ink-muted">Searching…</span>}
      {!picked && people.isError && (
        <span className="font-sans text-xs text-bw-danger">Search failed. Try again.</span>
      )}
      {!picked && people.data && people.data.length === 0 && (
        <span className="font-sans text-xs text-bw-ink-muted">Nobody matches.</span>
      )}
      {!picked && people.data && people.data.length > 0 && (
        <ul className="flex flex-col">
          {people.data.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                className="flex w-full flex-col items-start rounded-card px-2 py-1 text-left hover:bg-bw-navy/5"
                onClick={() => setPicked(person)}
              >
                <span className="font-sans text-sm text-bw-navy">{person.display_name ?? person.upn}</span>
                {person.upn && <span className="font-sans text-xs text-bw-ink-muted">{person.upn}</span>}
                {person.kind === "service_principal" && (
                  <span className="font-sans text-xs text-bw-ink-muted">Application (a headless agent's identity)</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {picked && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-sans text-xs text-bw-ink-muted">Give {picked.display_name ?? picked.upn}:</span>
          {TIERS.filter((tier) => picked.kind !== "service_principal" || tier.role === "agent").map((tier) => (
            <Button
              key={tier.role}
              variant={tier.role === "launcher" ? "default" : "secondary"}
              size="sm"
              disabled={grant.isPending}
              onClick={() =>
                grant.mutate(
                  { principalId: picked.id, role: tier.role },
                  {
                    onSuccess: () => {
                      setPicked(null);
                      setQuery("");
                    },
                  },
                )
              }
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              {grant.isPending ? "Granting…" : tier.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPicked(null);
              setQuery("");
            }}
          >
            Cancel
          </Button>
        </div>
      )}
      {grant.isError && (
        <span className="font-sans text-xs text-bw-danger">
          {typeof (grant.error.body as { detail?: unknown })?.detail === "string"
            ? String((grant.error.body as { detail: string }).detail)
            : "Couldn't grant that. Try again."}
        </span>
      )}
    </div>
  );
}

export function AdminAccess({ selfOid }: { selfOid?: string }) {
  const { data, isLoading, isError } = useAccess(true);
  const launcher = data?.assignments.filter((a) => a.role === "launcher") ?? [];
  const agents = data?.assignments.filter((a) => a.role === "agent") ?? [];
  const full = data?.assignments.filter((a) => a.role !== "launcher" && a.role !== "agent") ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-bw-blue" aria-hidden />
          Who can sign in
        </CardTitle>
        <p className="font-sans text-xs text-bw-ink-muted">
          Access is an Entra assignment on the North Star app. Project Launcher only means the Launches page and
          the launch-video tools, nothing else. AI agent is for an agent's own account: it answers the survey
          only as a persona registered below, never as a person. Changes reach a person with their next sign-in
          {data?.token_lag_minutes ? <>; a session already open keeps its old access for up to {data.token_lag_minutes} minutes</> : null}.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading access…</p>}
        {isError && <p className="font-sans text-sm text-bw-ink-muted">Couldn't load access.</p>}
        {data && !data.enabled && (
          <p className="rounded-card border border-bw-blue-muted/40 p-3 font-sans text-sm text-bw-ink-muted">
            Not available here: {data.reason ?? "the app's identity cannot reach Microsoft Graph."}
          </p>
        )}
        {data?.enabled && (
          <>
            {data.assignment_required === false && (
              <p className="font-sans text-xs text-bw-danger">
                Entra is not requiring assignment on this app: anyone in the tenant can sign in, and the
                list below only sets tiers.
              </p>
            )}
            <AddPerson />
            <div className="flex flex-col gap-1">
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                Project Launcher only
              </span>
              {launcher.length === 0 && (
                <span className="font-sans text-xs text-bw-ink-muted">Nobody yet.</span>
              )}
              {launcher.map((a) => (
                <AssignmentRow key={a.id} assignment={a} isSelf={isSelf(a, selfOid)} />
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                Full access
              </span>
              {full.length === 0 && <span className="font-sans text-xs text-bw-ink-muted">Nobody yet.</span>}
              {full.map((a) => (
                <AssignmentRow key={a.id} assignment={a} isSelf={isSelf(a, selfOid)} />
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
                AI agents
              </span>
              {agents.length === 0 && <span className="font-sans text-xs text-bw-ink-muted">None yet.</span>}
              {agents.map((a) => (
                <AssignmentRow key={a.id} assignment={a} isSelf={isSelf(a, selfOid)} />
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
